import FindConversationResponseMessage from 'owa-service/lib/contract/FindConversationResponseMessage';
import getConversationSortByProperty from '../../../utils/tables/getConversationSortByProperty';
import onLoadInitialRowsSucceeded from '../onLoadInitialRowsSucceeded';
import RequestOptions from 'owa-service/lib/RequestOptions';
import { action } from 'satcheljs/lib/legacy';
import { DatapointStatus } from 'owa-analytics';
import {
    getBaseFolderId,
    getBaseServerFolderId,
    TableView,
    MailFolderTableQuery,
    listViewStore,
    isFolderPaused,
    MailSortHelper,
} from 'owa-mail-list-store';
import { getMailboxInfoFromTableQuery } from 'owa-mail-mailboxinfo';
import { INITIAL_LOAD_ROW_COUNT } from '../../data-loading/TableLoadingConstants';
import { isFeatureEnabled } from 'owa-feature-flags';
import { OnInitialTableLoadComplete } from 'owa-mail-loading-action-types';
import { PerformanceDatapoint } from 'owa-analytics';
import { ServiceFetchError } from 'owa-service/lib/ServiceFetchError';
import {
    findConversationWithStartIndex,
    FindConversationOperationEndLabel,
    FindConversationOperationStartLabel,
    lazyFindConversationWithSeekToDateTimeCondition,
} from 'owa-mail-store';
import getHashtagsToExclude from 'owa-mail-inbox-plus-store/lib/selectors/getHashtagsToExclude';
import getHashtagsToInclude from 'owa-mail-inbox-plus-store/lib/selectors/getHashtagsToInclude';

/**
 * Load the initial conversations from the server
 * @param tableView to load conversations in
 * @param OnInitialTableLoadComplete is a callback that is called when we receive the response
 * The callback is handled by table loading
 * @param isTablePrefetched indicates if the table is prefetched
 * @return a promise that resolves when the load table from server has completed
 */
export default action('loadInitialConversationsFromServer')(
    function loadInitialConversationsFromServer(
        tableView: TableView,
        isTablePrefetched: boolean,
        onInitialTableLoadComplete: OnInitialTableLoadComplete
    ): Promise<void> {
        const mailTableQuery = tableView.tableQuery as MailFolderTableQuery;

        //Datapoint is Verbose to enable logging of waterfall timings
        const findConversationDatapoint = new PerformanceDatapoint('TnS_FindConversation', {
            logVerbose: true,
        });
        const isFilter = mailTableQuery.viewFilter && mailTableQuery.viewFilter !== 'All';
        const { sortColumn, sortDirection } = MailSortHelper.getDefaultSortBy();
        const isSort =
            mailTableQuery.sortBy &&
            (mailTableQuery.sortBy.sortColumn !== sortColumn ||
                mailTableQuery.sortBy.sortDirection !== sortDirection);

        findConversationDatapoint.addCustomProperty('scenarioType', mailTableQuery.scenarioType);
        findConversationDatapoint.addCustomProperty('isPrefetch', isTablePrefetched);
        findConversationDatapoint.addCustomProperty('isFilter', isFilter);
        findConversationDatapoint.addCustomProperty('isSort', isSort);

        // https://msfast.visualstudio.com/FAST/_workitems/edit/260700: Remove this line once flight is 100% WW
        findConversationDatapoint.addCustomProperty(
            'isRoamingFavoritesEnabled',
            isFeatureEnabled('tri-favorites-roaming')
        );

        let requestOptions = getMailboxInfoFromTableQuery(mailTableQuery).requestOptions;
        if (requestOptions) {
            requestOptions.datapoint = {
                customData: {
                    scenarioType: mailTableQuery.scenarioType,
                    isPrefetch: isTablePrefetched,
                    isFilter: isFilter,
                    isSort: isSort,
                    isRoamingFavoritesEnabled: isFeatureEnabled('tri-favorites-roaming'), // https://msfast.visualstudio.com/FAST/_workitems/edit/260700: Remove this line once flight is 100% WW
                },
            };
        }

        let findConversationResponse;

        if (!isSort && isFolderPaused(tableView.tableQuery.folderId)) {
            findConversationResponse = lazyFindConversationWithSeekToDateTimeCondition.importAndExecute(
                getBaseFolderId(mailTableQuery),
                listViewStore.inboxPausedDateTime,
                INITIAL_LOAD_ROW_COUNT,
                mailTableQuery.viewFilter,
                getConversationSortByProperty(mailTableQuery),
                mailTableQuery.focusedViewFilter,
                mailTableQuery.requestShapeName,
                requestOptions,
                mailTableQuery.categoryName,
                getHashtagsToInclude(mailTableQuery.inboxClassificationFilter),
                getHashtagsToExclude(mailTableQuery.inboxClassificationFilter),
                getBaseServerFolderId(tableView, true /* returnNullIfSameAsFolderId */)
            );
        } else {
            findConversationResponse = findConversationWithStartIndex(
                getBaseFolderId(mailTableQuery),
                0 /* startIndex */,
                INITIAL_LOAD_ROW_COUNT,
                mailTableQuery.viewFilter,
                getConversationSortByProperty(mailTableQuery),
                mailTableQuery.focusedViewFilter,
                mailTableQuery.requestShapeName,
                mailTableQuery.shouldTryToUseSessionDataResponse,
                requestOptions,
                mailTableQuery.categoryName,
                getHashtagsToInclude(mailTableQuery.inboxClassificationFilter),
                getHashtagsToExclude(mailTableQuery.inboxClassificationFilter),
                findConversationDatapoint,
                getBaseServerFolderId(tableView, true /* returnNullIfSameAsFolderId */)
            );
        }

        return processFindConversationResponse(
            findConversationResponse,
            tableView,
            findConversationDatapoint,
            onInitialTableLoadComplete,
            isTablePrefetched
        );
    }
);

function processFindConversationResponse(
    findConversation: Promise<any>,
    tableView: TableView,
    findConversationDatapoint,
    onInitialTableLoadComplete: OnInitialTableLoadComplete,
    isTablePrefetched: boolean
): Promise<void> {
    return findConversation.then(
        (response: {
            findConversationResponseMessage: FindConversationResponseMessage;
            responseInit: RequestOptions;
            url: string;
        }) => {
            let promiseToReturn = Promise.resolve();
            const responseMessage = response.findConversationResponseMessage;

            const isSuccessResponseClass = responseMessage.ResponseClass == 'Success';
            if (isSuccessResponseClass) {
                // Explicitly logging request-id here. In case of error, it will be automatically logged
                // as part of findConversationDatapoint.endWithError()
                findConversationDatapoint.addCustomProperty(
                    'request-id',
                    (response.responseInit.headers as Headers).get('request-id')
                );

                // Explicitly log the time spent on server
                logServerTimings(
                    response.url,
                    response.responseInit.headers as Headers,
                    findConversationDatapoint
                );

                findConversationDatapoint.end();
                onLoadInitialRowsSucceeded(
                    tableView,
                    responseMessage.Conversations,
                    responseMessage.TotalConversationsInView,
                    responseMessage.SearchFolderId,
                    responseMessage.FolderId
                );
            } else {
                const error = new Error(
                    `ResponseCode=${responseMessage.ResponseCode}, Stacktrace=${
                        responseMessage.StackTrace
                    }`
                ) as ServiceFetchError;

                error.fetchErrorType = 'ServerFailure';
                error.response = new Response(undefined, response.responseInit);

                findConversationDatapoint.endWithError(DatapointStatus.ServerError, error);

                promiseToReturn = Promise.reject(error);
            }

            onInitialTableLoadComplete(
                tableView,
                isSuccessResponseClass,
                responseMessage.ResponseCode,
                isTablePrefetched
            );

            return promiseToReturn;
        },
        error => {
            // Server returned non-200 response
            findConversationDatapoint.endWithError(DatapointStatus.ServerError, error);
            const responseCode = (error && error.response && error.response.status) || '';
            onInitialTableLoadComplete(tableView, false, responseCode, isTablePrefetched);

            return Promise.reject(error);
        }
    );
}

function logServerTimings(url: string, headers: Headers, dataPoint: PerformanceDatapoint): void {
    if (headers) {
        logTimeDifferenceForHeaders(
            headers,
            dataPoint,
            'x-frontend-begin',
            'x-frontend-end',
            'serverFETimeElapsed'
        );

        logTimeDifferenceForHeaders(
            headers,
            dataPoint,
            'x-backend-begin',
            'x-backend-end',
            'serverBETimeElapsed'
        );
    }

    if (dataPoint.waterfallTimings) {
        const findConversationOperationStart =
            dataPoint.waterfallTimings[FindConversationOperationStartLabel];
        const findConversationOperationEnd =
            dataPoint.waterfallTimings[FindConversationOperationEndLabel];

        if (
            findConversationOperationStart !== undefined &&
            findConversationOperationEnd !== undefined
        ) {
            dataPoint.addCustomProperty(
                'findConversationOperationTimeElapsed',
                Math.abs(findConversationOperationEnd - findConversationOperationStart)
            );
        }
    }
}

function logTimeDifferenceForHeaders(
    headers: Headers,
    dataPoint: PerformanceDatapoint,
    startHeader: string,
    endHeader: string,
    propertyName: string
) {
    const startTime = headers.get(startHeader);
    const endTime = headers.get(endHeader);

    const startDate = startTime ? new Date(startTime) : undefined;
    const endDate = endTime ? new Date(endTime) : undefined;

    if (startDate && endDate) {
        const delta = Math.abs(endDate.getTime() - startDate.getTime());
        dataPoint.addCustomProperty(propertyName, delta);
    }
}
